import useSWR from "swr";
import { fetcher } from "../../../../services/fetcher";
import { WalletEndpoint_API } from "../../../../api/endpoints";
import { useOutletContext } from "react-router-dom";
import { useSelector } from "react-redux";
import { Paper, SimpleGrid, Text, rem } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import MainLoader from "../../../../common/components/Loader/Loader";
import DataWrapper from "../../../../common/components/DataWrapper/DataWrapper";
import classes from "./Wallet.module.css";
import { filterOptions } from "../../../master-wallet/utils/transaction";
import { useMemo, useState } from "react";

const cellNowrap = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const fmtAmount = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n.toLocaleString("en-IN") : "0";
};

const addFundEndpoint = "asc-wallet/user-wallet/transactions/";

const Wallet = () => {
  const isNarrow = useMediaQuery("(max-width: 48em)");
  const outletContext = useOutletContext();
  const user = outletContext ? outletContext.user : null;
  const { user_type } = useSelector((state) => state.auth);
  const userProfile = useSelector((state) => !user && state.userProfile);

  const email = user
    ? user.user_profile_info.user.email
    : userProfile.user.email;

  const isSuperUser = user_type[0].includes("is_superuser");

  const walletURL = isSuperUser
    ? `${WalletEndpoint_API}?email=${email}`
    : `${WalletEndpoint_API}`;

  const transactionURL = isSuperUser
    ? `${addFundEndpoint}?user_email=${email}`
    : `${addFundEndpoint}`;

  const {
    data: wallet,
    isLoading: isWalletLoading,
  } = useSWR(walletURL, fetcher);

  const [transactionsData, setTransactionsData] = useState(null);

  const config = useMemo(
    () => ({
      searchInputWidth: isNarrow ? "100%" : rem(248),
      searchInputMaxWidth: isNarrow ? "100%" : undefined,
      toolbarLeftGap: "sm",
      tableLayout: isNarrow ? "auto" : "fixed",
      tableMinWidth: isNarrow ? rem(520) : undefined,
      horizontalSpacing: isNarrow ? "sm" : "md",
      verticalSpacing: isNarrow ? 6 : "xs",
      fields: [
        { key: "transaction_number", label: "Transaction number", width: rem(130), cellStyle: cellNowrap },
        { key: "amount", label: "Amount", width: rem(88), cellStyle: cellNowrap },
        { key: "transaction_type", label: "Transaction type", width: rem(110), cellStyle: cellNowrap },
        { key: "created_by", label: "Created by", width: rem(100), cellStyle: cellNowrap },
        { key: "created_at", label: "Date", type: "datetime", width: rem(120), cellStyle: cellNowrap },
      ],
    }),
    [isNarrow]
  );

  if (isWalletLoading) {
    return <MainLoader />;
  }

  const transactionResults = Array.isArray(transactionsData?.results)
    ? transactionsData.results
    : [];
  const totalAdded = transactionResults.reduce((sum, row) => {
    const type = String(row?.transaction_type || "").toLowerCase();
    const amount = Number(row?.amount || 0);
    if (type.includes("add") || type.includes("credit")) {
      return sum + amount;
    }
    return sum;
  }, 0);
  const totalDeduct = transactionResults.reduce((sum, row) => {
    const type = String(row?.transaction_type || "").toLowerCase();
    const amount = Number(row?.amount || 0);
    if (type.includes("deduct") || type.includes("debit") || type.includes("withdraw")) {
      return sum + amount;
    }
    return sum;
  }, 0);

  return (
    <div className={classes.walletPage}>
      <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md" mt="lg">
        <Paper className={classes.statCard} radius="lg">
          <div className={classes.statIcon}>BAL</div>
          <div className={classes.statText}>
            <Text className={classes.statValue}>
              {wallet?.currency_type || "₹"} {fmtAmount(wallet?.balance)}
            </Text>
            <Text className={classes.statLabel}>Wallet Amount</Text>
          </div>
        </Paper>
        <Paper className={classes.statCard} radius="lg">
          <div className={`${classes.statIcon} ${classes.statIconUp}`}>+</div>
          <div className={classes.statText}>
            <Text className={classes.statValue}>₹{fmtAmount(totalAdded)}</Text>
            <Text className={classes.statLabel}>Total Add Fund</Text>
          </div>
        </Paper>
        <Paper className={classes.statCard} radius="lg">
          <div className={`${classes.statIcon} ${classes.statIconCal}`}>-</div>
          <div className={classes.statText}>
            <Text className={classes.statValue}>₹{fmtAmount(totalDeduct)}</Text>
            <Text className={classes.statLabel}>Total Deduct Fund</Text>
          </div>
        </Paper>
      </SimpleGrid>

      <div className={classes.historyHeader}>
        <div>
          <Text className={classes.historyTitle} mt="lg">
            Transaction History
          </Text>
        </div>
      </div>

      <Paper className={classes.tableCard} radius="lg" mt="lg">
        <DataWrapper
          endpoint={transactionURL}
          config={config}
          filterOptions={filterOptions}
          onData={setTransactionsData}
        />
      </Paper>
    </div>
  );
};

export default Wallet;